import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useAccess } from "./access";

export interface Need {
  solution: string;
  key: string;
  label: string;
  count: number;
  to: string;
}

export function useNeeds() {
  return useQuery({ queryKey: ["needs"], queryFn: () => api<{ needs: Need[] }>("/needs").then((r) => r.needs), staleTime: 60_000, refetchInterval: 120_000 });
}

/**
 * Home's "Needs you": one chip for each kind of thing waiting on a person, with its count, opening the page where it
 * is dealt with. Counted from each solution's data, never written by AI; a chip shows only when its count is above
 * nought and the person can open where it leads.
 */
export function NeedsYou({ bare }: { bare?: boolean } = {}) {
  const { canOpen } = useAccess();
  const q = useNeeds();
  const [all, setAll] = useState(false);
  if (!q.data) return null;
  const every = q.data.filter((n) => n.count > 0 && canOpen(n.to.split("?")[0] ?? n.to));
  // Under Home's ask box only the first few show (approvals first), with the rest one tap away.
  const FEW = 5;
  const chips = bare && !all ? every.slice(0, FEW) : every;
  const more = every.length - chips.length;
  // Bare: just the chips, centred under Home's ask box (nothing when nothing waits).
  if (bare && every.length === 0) return null;
  return (
    <section className={`needs${bare ? " needs--bare" : ""}`} aria-label="Needs you">
      {!bare && <span className="needs__label">Needs you</span>}
      {chips.length === 0 ? (
        <span className="muted needs__none">Nothing is waiting on you.</span>
      ) : (
        <ul className="needs__list">
          {chips.map((x) => (
            <li key={`${x.solution}.${x.key}`}>
              <Link className="al-btn needs__chip" to={x.to}>
                {x.label}
                <b className="needs__n">{x.count.toLocaleString("en-AU")}</b>
              </Link>
            </li>
          ))}
          {more > 0 && (
            <li>
              <button type="button" className="al-btn needs__chip needs__more" onClick={() => setAll(true)}>
                {more} more
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
